// Day-8: 通过 WSClient 发 list_dirs / list_sessions RPC，等回 dirs_list / sessions_list 帧。
//
// 实现：
//   - 发送时拼一个 envelope，记下 id
//   - 在 onMessage 回调里检查 ack_id 匹配，resolve Promise
//   - 30 秒超时

import type { WSClient, Envelope } from "./ws";

export interface DirEntry {
  path: string;
  name: string;
  kind: string;
  has_git: boolean;
  has_claude_md: boolean;
  session_count: number;
  last_session_ts: number;
  mtime: number;
}

export interface SessionEntry {
  id: string;
  ai_title: string;
  first_user_msg: string;
  message_count: number;
  started_ts: number;
  updated_ts: number;
  size: number;
}

interface PendingRPC {
  resolve: (env: Envelope) => void;
  reject: (e: Error) => void;
  timer: number;
}

export class CliRPC {
  private pending = new Map<string, PendingRPC>();

  constructor(private ws: WSClient) {}

  /** 在 WSClient 的 onMessage 回调里调一下，让 RPC 能解析回包。 */
  handleEnvelope(env: Envelope): boolean {
    if (!env.ack_id) return false;
    const pending = this.pending.get(env.ack_id);
    if (!pending) return false;
    clearTimeout(pending.timer);
    this.pending.delete(env.ack_id);
    pending.resolve(env);
    return true; // 标记"我处理了"
  }

  private sendAndWait<P>(type: string, payload: P, timeoutMs = 30000): Promise<Envelope> {
    return new Promise((resolve, reject) => {
      // 用 ws.send 但需要拿到 envelope.id 才能等回包。
      // ws.send 内部生成 id 但不返回——这里改用直接构造 + 走 ws 的 raw send。
      const id = `rpc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const env = {
        v: 1,
        type,
        id,
        ts: Date.now(),
        payload,
      };
      const timer = window.setTimeout(() => {
        this.pending.delete(id);
        reject(new Error(`rpc timeout: ${type} after ${timeoutMs}ms`));
      }, timeoutMs);
      this.pending.set(id, { resolve, reject, timer });
      try {
        this.ws.sendRaw(env);
      } catch (e) {
        clearTimeout(timer);
        this.pending.delete(id);
        reject(e as Error);
      }
    });
  }

  async listDirs(targetAgentId: string): Promise<{
    agent_id: string;
    dirs: DirEntry[];
    default_cwd: string;
  }> {
    const env = await this.sendAndWait("list_dirs", { target_agent_id: targetAgentId });
    if (env.type === "error") {
      const p = env.payload as { code: string; msg: string };
      throw new Error(`${p.code}: ${p.msg}`);
    }
    return env.payload as any;
  }

  async listSessions(targetAgentId: string, cwd: string): Promise<{
    agent_id: string;
    cwd: string;
    sessions: SessionEntry[];
  }> {
    const env = await this.sendAndWait("list_sessions", {
      target_agent_id: targetAgentId,
      cwd,
    });
    if (env.type === "error") {
      const p = env.payload as { code: string; msg: string };
      throw new Error(`${p.code}: ${p.msg}`);
    }
    return env.payload as any;
  }
}
